import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { adjoinPropTypes } from "../Adjoin";
import { Transition } from "../Transition";
import styles from "./Show.module.scss";

/**
 * Show will render content, typically an overlay, by transitioning it with an animation. Show should be provided a
 * location from where the overlay will expand from. For example, when Show is rendered "below", the content will grow
 * slightly down when its rendered.
 */

export const Show = React.forwardRef((props, ref) => {
  const { children, className, duration = 150, id, location, onTransformationEnd } = props;

  return (
    <div className={getClass()} ref={ref}>
      <Transition {...getTransitionProps()}>{children}</Transition>
    </div>
  );

  function getTransitionProps() {
    return {
      animate: getAnimateValues(),
      className: getContentClass(),
      id,
      initial: getInitialValues(),
      onTransformationEnd,
      transition: getTransition(),
    };
  }

  function getClass() {
    return classNames(className, styles.outerContainer);
  }

  function getContentClass() {
    return classNames(styles.content, {
      [styles.contentOpen]: location !== "hidden",
    });
  }

  function getAnimateValues() {
    return location === "hidden" ? { opacity: 0, x: 1, y: -2 } : { opacity: 1, x: 0, y: 0 };
  }

  function getInitialValues() {
    const offsets = {
      above: { opacity: 0, x: 1, y: -2 },
      below: { opacity: 0, x: 1, y: 2 },
      left: { opacity: 0, x: -2, y: 1 },
      right: { opacity: 0, x: 2, y: 1 },
    };

    return offsets[location] || offsets.below;
  }

  function getTransition() {
    return {
      duration: duration / 1000,
    };
  }
});

Show.displayName = "Show";
Show.propTypes = {
  /** Content to be rendered. This is the content that Transform will act upon. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Length of animation for Show's render of its play list. */
  duration: PropTypes.number,

  /** Unique identifier which is passed to PlayList when it's necessary to use a new playList. */
  id: PropTypes.string,

  /** Required location where content is rendered once "opened". */
  location: adjoinPropTypes.location,

  /** Function called once Show's render animation is complete. */
  onTransformationEnd: PropTypes.func,
};
